import { config } from "./config";
import type { PlanId } from "./funnelData";
import { toWorkerLandingAttribution, type LandingAttribution } from "./attribution";

type FlowVariant = "a" | "b";

type CreateCheckoutSessionRequest = {
  email?: string;
  leadId?: number;
  planId: PlanId;
  flow: FlowVariant;
  currency?: string;
  offer?: string;
  value?: number;
  eventId?: string;
  fbp?: string;
  fbc?: string;
  externalId?: string;
  pageUrl?: string;
  attribution?: LandingAttribution;
};

type CreateCheckoutSessionResponse = {
  checkoutUrl: string;
  sessionId: string;
  completionToken: string;
};

export type StripePlanPrice = {
  priceId: string;
  productId: string;
  productName: string | null;
  unitAmount: number;
  currency: string;
};

export type StripePricesResponse = Partial<Record<PlanId, StripePlanPrice>>;

export type CompleteCheckoutSessionResponse = {
  sessionId: string;
  paymentStatus: string;
  refundStatus: string;
  emailSent: boolean;
  planId: PlanId;
  amountTotal: number | null;
  purchaseValue: number | null;
  currency: string | null;
  metaEventId: string | null;
};

export const createCheckoutSession = async (
  request: CreateCheckoutSessionRequest
): Promise<CreateCheckoutSessionResponse> => {
  if (!config.apiBaseUrl) {
    throw new Error("VITE_API_BASE_URL is not configured");
  }

  const { attribution, ...requestBody } = request;
  const workerAttribution = toWorkerLandingAttribution(attribution);
  const response = await fetch(`${config.apiBaseUrl}/api/checkout/create-session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...requestBody,
      ...(workerAttribution ? { attribution: workerAttribution } : {})
    })
  });

  if (!response.ok) {
    let detail = "";
    try {
      const body = (await response.json()) as { error?: string };
      detail = body.error ? `: ${body.error}` : "";
    } catch {
      // Non-JSON error body.
    }
    throw new Error(`Failed to create checkout session: ${response.status}${detail}`);
  }

  return response.json() as Promise<CreateCheckoutSessionResponse>;
};

export const fetchPrices = async (offer?: string): Promise<StripePricesResponse> => {
  if (!config.apiBaseUrl) {
    throw new Error("VITE_API_BASE_URL is not configured");
  }

  const params = new URLSearchParams();
  if (offer) params.set("offer", offer);
  const query = params.toString();
  const response = await fetch(`${config.apiBaseUrl}/api/checkout/prices${query ? `?${query}` : ""}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch prices: ${response.status}`);
  }

  return response.json() as Promise<StripePricesResponse>;
};

/**
 * Meta identifiers re-sent from the success page. The `_fbp` cookie is reliably present by
 * this point, so passing them backfills the server-side Purchase (and payment record) when
 * the earlier checkout-session snapshot captured them before `fbevents.js` had landed.
 */
type CheckoutCompleteTracking = {
  fbp?: string;
  fbc?: string;
  externalId?: string;
};

export const completeCheckoutSession = async (
  sessionId: string,
  completionToken?: string,
  tracking?: CheckoutCompleteTracking
): Promise<CompleteCheckoutSessionResponse> => {
  if (!config.apiBaseUrl) {
    throw new Error("VITE_API_BASE_URL is not configured");
  }

  const response = await fetch(`${config.apiBaseUrl}/api/payments/checkout-complete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionId,
      ...(completionToken ? { completionToken } : {}),
      ...(tracking?.fbp ? { fbp: tracking.fbp } : {}),
      ...(tracking?.fbc ? { fbc: tracking.fbc } : {}),
      ...(tracking?.externalId ? { externalId: tracking.externalId } : {})
    })
  });

  if (!response.ok) {
    throw new Error(`Failed to verify checkout session: ${response.status}`);
  }

  return response.json() as Promise<CompleteCheckoutSessionResponse>;
};

/** Server-side Meta CAPI ViewContent (dedup with browser `ViewContent` via matching `eventId`). */
export const trackViewContentServerEvent = async (payload: {
  eventId: string;
  contentIds: PlanId[];
  value: number;
  currency: string;
  contentName?: string;
  fbp?: string | null;
  fbc?: string | null;
  externalId?: string;
  pageUrl?: string;
}): Promise<void> => {
  if (!config.apiBaseUrl) return;

  const response = await fetch(`${config.apiBaseUrl}/api/meta/view-content`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    let detail = "";
    try {
      const body = (await response.json()) as { error?: string };
      detail = body.error ? `: ${body.error}` : "";
    } catch {
      //
    }
    throw new Error(`Failed to track ViewContent server event: ${response.status}${detail}`);
  }
};

/** Server-side Meta CAPI Lead (dedup with browser fbq Lead via matching event id). */
export const trackLeadServerEvent = async (payload: {
  email: string;
  eventId: string;
  fbp?: string | null;
  fbc?: string | null;
  externalId?: string;
  pageUrl?: string;
}): Promise<void> => {
  if (!config.apiBaseUrl) return;

  const response = await fetch(`${config.apiBaseUrl}/api/leads/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    let detail = "";
    try {
      const body = (await response.json()) as { error?: string };
      detail = body.error ? `: ${body.error}` : "";
    } catch {
      //
    }
    throw new Error(`Failed to track lead server event: ${response.status}${detail}`);
  }
};

export const trackPageViewServerEvent = async (payload: {
  eventId: string;
  fbp?: string | null;
  fbc?: string | null;
  externalId?: string;
  pageUrl?: string;
}): Promise<void> => {
  if (!config.apiBaseUrl) return;

  const response = await fetch(`${config.apiBaseUrl}/api/meta/page-view`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    let detail = "";
    try {
      const body = (await response.json()) as { error?: string };
      detail = body.error ? `: ${body.error}` : "";
    } catch {
      //
    }
    throw new Error(`Failed to track PageView server event: ${response.status}${detail}`);
  }
};

export const captureLead = async (payload: {
  email: string;
  onboardingResponses: unknown;
  source: string;
  attribution?: LandingAttribution;
}): Promise<{ leadId: number | null }> => {
  if (!config.apiBaseUrl) {
    throw new Error("VITE_API_BASE_URL is not configured");
  }

  const { attribution, ...requestBody } = payload;
  const workerAttribution = toWorkerLandingAttribution(attribution);
  const response = await fetch(`${config.apiBaseUrl}/api/leads/capture`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...requestBody,
      ...(workerAttribution ? { attribution: workerAttribution } : {})
    })
  });

  if (!response.ok) {
    throw new Error(`Failed to save lead: ${response.status}`);
  }

  return response.json() as Promise<{ leadId: number | null }>;
};
